import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../supabaseClient';

const AdminGallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    category: 'Signature Tower',
    title: '',
    file: null
  });

  const categories = [
    'Signature Tower',
    'Residential Projects',
    'Commercial Projects',
    'Site Progress',
    'Events',
    'Awards'
  ];

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('gallery')
        .select('*')
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      setImages(data || []);
    } catch (err) {
      console.error('Error fetching gallery:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFormData({ ...formData, file: e.target.files[0] });
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!formData.file) {
      alert('Please select an image to upload.');
      return;
    }

    setUploading(true);
    try {
      // 1. Upload to Supabase Storage
      const fileExt = formData.file.name.split('.').pop();
      const fileName = `gallery_${Date.now()}.${fileExt}`;
      const filePath = `gallery/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('hbb_files')
        .upload(filePath, formData.file);

      if (uploadError) throw uploadError;

      // 2. Get Public URL
      const { data: publicUrlData } = supabase.storage
        .from('hbb_files')
        .getPublicUrl(filePath);

      const imageUrl = publicUrlData.publicUrl;

      // 3. Insert into database
      const { error: dbError } = await supabase
        .from('gallery')
        .insert([{
          category: formData.category,
          title: formData.title,
          image_url: imageUrl
        }]);

      if (dbError) throw dbError;

      // Success
      alert('Image uploaded successfully!');
      setFormData({ ...formData, title: '', file: null });
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchImages();

    } catch (error) {
      console.error('Upload failed:', error);
      alert('Upload failed: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id, imageUrl) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    
    try {
      // 1. Delete from storage (extract path from URL)
      const pathParts = imageUrl.split('hbb_files/');
      if (pathParts.length === 2) {
        const filePath = pathParts[1];
        await supabase.storage.from('hbb_files').remove([filePath]);
      }

      // 2. Delete from database
      const { error } = await supabase
        .from('gallery')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setImages(images.filter(img => img.id !== id));
    } catch (err) {
      console.error('Delete failed:', err);
      alert('Failed to delete image.');
    }
  };

  // Group images by category for display
  const groupedImages = images.reduce((acc, img) => {
    if (!acc[img.category]) acc[img.category] = [];
    acc[img.category].push(img);
    return acc;
  }, {});

  return (
    <div className="p-8 min-h-screen bg-[#131313] text-white flex flex-col gap-8">
      <h2 className="text-3xl font-bold text-[#f59e0b]" style={{ fontFamily: 'Bebas Neue' }}>Gallery Management</h2>

      {/* Upload Section */}
      <div className="bg-[#1c1b1b] p-6 rounded-xl border border-white/10 shadow-xl max-w-4xl">
        <h3 className="text-xl font-semibold mb-6 text-[#d8c3ad]">Upload New Image</h3>
        <form onSubmit={handleUpload} className="grid grid-cols-1 md:grid-cols-2 gap-6">
          
          <div className="md:col-span-1">
            <label className="block text-gray-400 text-sm font-bold mb-2">Category / Section</label>
            <input 
              type="text" 
              name="category" 
              value={formData.category} 
              onChange={handleInputChange} 
              list="category-options"
              className="bg-[#0e0e0e] border border-white/10 rounded px-4 py-3 text-white w-full focus:border-[#f59e0b] focus:outline-none" 
              required
            />
            <datalist id="category-options">
              {categories.map(cat => <option key={cat} value={cat} />)}
            </datalist>
            <p className="text-xs text-gray-500 mt-1">Select from list or type a new category.</p>
          </div>

          <div className="md:col-span-1">
            <label className="block text-gray-400 text-sm font-bold mb-2">Image Title (Optional)</label>
            <input 
              type="text" 
              name="title" 
              value={formData.title} 
              onChange={handleInputChange} 
              placeholder="E.g., Aerial view of phase 1"
              className="bg-[#0e0e0e] border border-white/10 rounded px-4 py-3 text-white w-full focus:border-[#f59e0b] focus:outline-none" 
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-gray-400 text-sm font-bold mb-2">Select Image</label>
            <input 
              type="file" 
              accept="image/*"
              onChange={handleFileChange}
              ref={fileInputRef}
              className="bg-[#0e0e0e] border border-white/10 rounded px-4 py-3 text-white w-full focus:border-[#f59e0b] focus:outline-none" 
              required
            />
          </div>

          <div className="md:col-span-2 flex justify-end mt-2">
            <button 
              type="submit" 
              disabled={uploading}
              className={`font-bold py-3 px-8 rounded transition-colors flex items-center gap-2 ${uploading ? 'bg-gray-600 text-gray-400 cursor-not-allowed' : 'bg-[#f59e0b] text-[#2a1700] hover:bg-[#ffc174]'}`}
            >
              {uploading ? (
                <><span className="material-symbols-outlined animate-spin text-sm">sync</span> Processing...</>
              ) : (
                <><span className="material-symbols-outlined text-sm">upload</span> Upload Image</>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Gallery View */}
      {loading ? (
        <div className="flex justify-center py-20"><span className="material-symbols-outlined animate-spin text-4xl text-[#d8c3ad]">sync</span></div>
      ) : Object.keys(groupedImages).length === 0 ? (
        <div className="bg-[#1c1b1b] p-12 rounded-xl border border-white/10 text-center max-w-4xl">
          <span className="material-symbols-outlined text-5xl text-gray-600 mb-4">collections</span>
          <p className="text-gray-400">No images uploaded yet.</p>
        </div>
      ) : (
        <div className="space-y-10 max-w-7xl">
          {Object.entries(groupedImages).map(([cat, imgs]) => (
            <div key={cat} className="bg-[#1c1b1b] p-6 rounded-xl border border-white/10">
              <h3 className="text-xl font-bold text-white mb-6 uppercase tracking-wider border-b border-gray-800 pb-2">{cat}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {imgs.map(img => (
                  <div key={img.id} className="relative group rounded overflow-hidden border border-gray-800 bg-[#0e0e0e]">
                    <div className="aspect-[4/3] w-full">
                      <img src={img.image_url} alt={img.title || cat} className="w-full h-full object-cover" />
                    </div>
                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-black/80 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-center items-center p-4 gap-3 text-center backdrop-blur-sm">
                      {img.title && <p className="text-xs text-white font-medium">{img.title}</p>}
                      <button 
                        onClick={() => handleDelete(img.id, img.image_url)}
                        className="bg-red-500/20 text-red-400 border border-red-500/50 rounded px-3 py-1 text-xs hover:bg-red-500 hover:text-white transition-colors flex items-center gap-1"
                      >
                        <span className="material-symbols-outlined text-[14px]">delete</span> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminGallery;
